import * as THREE from 'three';
import * as SceneModule from '../scene.js';
import { pointMaterial } from './material.js';
import { state, rebuildTiling, scrollOffset } from './tiling.js';

// Heightmap state
let currentHeightmapTexture = null;
let heightmapCanvas = null;
let heightmapCtx = null;
let heightmapWidth = 0;
let heightmapHeight = 0;
let heightmapData = null; // Cached pixel data for fast sampling

// Sample terrain height at a world XZ position using bilinear interpolation
export function sampleTerrainHeight(worldX, worldZ) {
  if (!heightmapData || !heightmapWidth || !heightmapHeight) {
    return null; // No heightmap loaded yet
  }
  
  const tileSize = state.tileSize;
  
  // Account for scroll offset - the terrain moves, so we need to sample
  // relative to the scrolling position
  const relX = worldX + scrollOffset.x;
  const relZ = worldZ + scrollOffset.y;
  
  // Convert to UV coordinates within [0,1] - the texture repeats per tile
  // The UV offset depends on whether tilesX/tilesZ are odd or even:
  // - Odd tile count: tile center at origin, so +0.5 offset needed
  // - Even tile count: tile edge at origin, so no offset needed
  const uOffset = (state.tilesX % 2) * 0.5;
  const vOffset = (state.tilesZ % 2) * 0.5;
  
  let u = (((relX / tileSize) + uOffset) % 1 + 1) % 1;
  let v = (((relZ / tileSize) + vOffset) % 1 + 1) % 1;
  
  // Convert UV to floating-point pixel coordinates for bilinear interpolation
  const fx = u * heightmapWidth - 0.5;
  const fy = v * heightmapHeight - 0.5;
  
  // Get the four surrounding pixels with wrapping
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  
  // Wrap coordinates for seamless tiling
  const wx0 = ((x0 % heightmapWidth) + heightmapWidth) % heightmapWidth;
  const wy0 = ((y0 % heightmapHeight) + heightmapHeight) % heightmapHeight;
  const wx1 = ((x1 % heightmapWidth) + heightmapWidth) % heightmapWidth;
  const wy1 = ((y1 % heightmapHeight) + heightmapHeight) % heightmapHeight;
  
  // Bilinear interpolation weights
  const tx = fx - x0;
  const ty = fy - y0;
  
  // Fast sample from cached heightmap data (RGBA, 4 bytes per pixel)
  const samplePixelHeight = (px, py) => {
    const idx = (py * heightmapWidth + px) * 4;
    return (heightmapData[idx] + heightmapData[idx + 1] + heightmapData[idx + 2]) / (255 * 3);
  };
  
  // Sample the four corners
  const h00 = samplePixelHeight(wx0, wy0);
  const h10 = samplePixelHeight(wx1, wy0);
  const h01 = samplePixelHeight(wx0, wy1);
  const h11 = samplePixelHeight(wx1, wy1);
  
  // Bilinear interpolation
  const h = h00 * (1 - tx) * (1 - ty) +
            h10 * tx * (1 - ty) +
            h01 * (1 - tx) * ty +
            h11 * tx * ty;
  
  const scale = pointMaterial.uniforms.uScale.value;
  const bias = pointMaterial.uniforms.uBias.value;
  
  return h * scale + bias;
}

export async function createTextureFromFile(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const texture = new THREE.Texture(bitmap);
    texture.needsUpdate = true;
    return texture;
  } catch (err) {
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.decoding = 'async';
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(e);
      i.src = dataUrl;
    });
    const texture = new THREE.Texture(img);
    texture.needsUpdate = true;
    return texture;
  }
}

export async function applyHeightmapTexture(texture) {
  const { renderer } = SceneModule;
  if (currentHeightmapTexture) {
    currentHeightmapTexture.dispose();
  }
  currentHeightmapTexture = texture;
  texture.colorSpace = THREE.NoColorSpace; // treat as data
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  // NPOT safety: use clamp + no mipmaps; POT can repeat+mips
  const img = texture.image;
  const isPOT = img && ( (img.width & (img.width - 1)) === 0 ) && ( (img.height & (img.height - 1)) === 0 );
  if (isPOT) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
  } else {
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
  }
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;

  pointMaterial.uniforms.uMap.value = texture;
  pointMaterial.uniforms.uBias.value = -pointMaterial.uniforms.uScale.value * 0.5;
  pointMaterial.needsUpdate = true;
  rebuildTiling();
  
  // Create canvas for CPU-side heightmap sampling and cache pixel data
  const texImg = texture.image;
  if (texImg) {
    heightmapWidth = texImg.width || texImg.naturalWidth || 512;
    heightmapHeight = texImg.height || texImg.naturalHeight || 512;
    heightmapCanvas = document.createElement('canvas');
    heightmapCanvas.width = heightmapWidth;
    heightmapCanvas.height = heightmapHeight;
    heightmapCtx = heightmapCanvas.getContext('2d', { willReadFrequently: true });
    heightmapCtx.drawImage(texImg, 0, 0, heightmapWidth, heightmapHeight);
    // Cache the entire heightmap for fast sampling
    heightmapData = heightmapCtx.getImageData(0, 0, heightmapWidth, heightmapHeight).data;
  }
}

function handleDropFile(file) {
  if (!file || !file.type.startsWith('image/')) return;
  createTextureFromFile(file)
    .then(applyHeightmapTexture)
    .catch((err) => console.error('Failed to load dropped heightmap', err));
}

export function setupHeightmapHandlers() {
  const { renderer } = SceneModule;
  const fileInput = document.getElementById('heightmapFile');
  
  fileInput.addEventListener('change', async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      const texture = await createTextureFromFile(file);
      await applyHeightmapTexture(texture);
      console.log('Heightmap loaded:', file.name);
    } catch (err) {
      console.error('Failed to load heightmap texture', err);
    }
  });

  renderer.domElement.addEventListener('dragover', (e) => {
    e.preventDefault();
    renderer.domElement.classList.add('drag-over');
  });

  renderer.domElement.addEventListener('dragleave', () => {
    renderer.domElement.classList.remove('drag-over');
  });

  renderer.domElement.addEventListener('drop', (e) => {
    e.preventDefault();
    renderer.domElement.classList.remove('drag-over');
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) {
      handleDropFile(file);
    }
  });
}
